import { formatNumber, Stack } from 'ui';

import { formatPercentageFeature } from '../../utils/formatFeature/formatFeature';
import { MaterialBreakdownItem } from '../MaterialBreakdownItem/MaterialBreakdownItem';
import { MaterialSampleBreakdownItem } from '../MaterialSampleBreakdownItem/MaterialSampleBreakdownItem';

const PET_SUM_KEYS: string[] = [
  'PET Bottle - Clear',
  'PET Bottle - Clear (Large)',
  'PET Bottle - Clear (Full Sleeve)',
  'PET Bottle - Light Blue',
  'PET Bottle - Dark Blue',
  'PET Bottle - Green',
  'PET Bottle - Other',
  'Non Food Grade PET Bottle',
];

const NON_PET_SUM_KEYS: string[] = ['Clear PET Tray', 'Film', 'Other Plastic', 'Non Plastic', 'Prohibitive', 'Liquid'];

export const PetMaterialSampleBreakdown = ({
  totalWeight,
  breakdown,
  precision = 1,
}: {
  totalWeight: number;
  breakdown: Record<string, number>;
  precision?: number;
}) => {
  const totalPET = PET_SUM_KEYS.reduce((acc, key) => acc + (breakdown[key] ?? 0), 0);
  const totalNonPET = NON_PET_SUM_KEYS.reduce((acc, key) => acc + (breakdown[key] ?? 0), 0);

  return (
    <Stack gap={2}>
      <div>
        <MaterialBreakdownItem
          label={'PET Bottle'}
          variant="bold"
          value={formatNumber(totalPET, {
            maximumFractionDigits: precision,
            minimumFractionDigits: precision,
            style: 'kilogram',
          })}
          secondaryValue={totalPET > 0 ? formatPercentageFeature(totalPET / totalWeight) : '0%'}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={
            (breakdown['PET Bottle - Clear'] ?? 0) +
            (breakdown['PET Bottle - Clear (Large)'] ?? 0) +
            (breakdown['PET Bottle - Clear (Full Sleeve)'] ?? 0)
          }
          label={'Clear'}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['PET Bottle - Light Blue']}
          label={'Light Blue'}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['PET Bottle - Dark Blue']}
          label={'Dark Blue'}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['PET Bottle - Green']}
          label={'Green'}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['PET Bottle - Other']}
          label={'Other'}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Non Food Grade PET Bottle']}
          label="Clear, non-food-grade"
          totalWeight={totalWeight}
        />
      </div>
      <div>
        <MaterialBreakdownItem
          label={'Non-PET Bottle'}
          variant="bold"
          value={formatNumber(totalNonPET, {
            maximumFractionDigits: precision,
            minimumFractionDigits: precision,
            style: 'kilogram',
          })}
          secondaryValue={totalNonPET > 0 ? formatPercentageFeature(totalNonPET / totalWeight) : '0%'}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Clear PET Tray']}
          label={'Clear PET Tray'}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown.Film}
          label={'Film'}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Other Plastic']}
          label={'Other Plastic'}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Non Plastic']}
          label={'Non Plastic'}
          totalWeight={totalWeight}
        />

        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown.Prohibitive}
          label={'Prohibitive'}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown.Liquid}
          label={'Liquid'}
          totalWeight={totalWeight}
        />
      </div>
      <MaterialBreakdownItem
        label="Total"
        value={formatNumber(totalWeight, {
          maximumFractionDigits: precision,
          minimumFractionDigits: precision,
          style: 'kilogram',
        })}
        secondaryValue={'100%'}
        variant="bold"
      />
    </Stack>
  );
};
